import type { FeatureTypeEnum, MenuTypeEnum } from '@/api/hanly/hanlyAPI.schemas';
import { FeatureFlagType } from '@/components/feature-flag/types';
import type { SettingsMenuItem } from '@/components/settings/settings-sidebar';

export interface MobileMenuPage {
  label: string;
  to: string;
  featureType?: FeatureTypeEnum;
  demoOnly?: boolean;
  // UI の機能フラグ（環境単位）。有効な環境でだけ表示する
  uiFeatureFlag?: FeatureFlagType;
  external?: boolean;
}

export interface MobileMenuSection {
  label: string;
  menuType?: MenuTypeEnum;
  featureType?: FeatureTypeEnum;
  demoOnly?: boolean;
  pages: MobileMenuPage[];
}

export interface MobileMenuSearchResult {
  label: string;
  to: string;
  context: string;
  external?: boolean;
}

export const RECENT_MENU_PAGES_MAX = 4;

export const mobileMenuSections: MobileMenuSection[] = [
  { label: '会話', demoOnly: true, pages: [{ label: '会話', to: '/chats' }] },
  {
    label: '見込客',
    featureType: 'CRM',
    pages: [
      { label: '見込客', to: '/leads' },
      { label: '営業リスト', to: '/leads/outreach-lists' },
    ],
  },
  {
    label: '案件',
    menuType: 'PROJECTS',
    pages: [
      { label: '案件', to: '/projects' },
      { label: '原価', to: '/projects/costs' },
    ],
  },
  {
    label: '受注',
    menuType: 'ORDERS',
    pages: [
      { label: '見積書', to: '/orders/quotations' },
      { label: 'EDI発注', to: '/orders/edi-purchase-orders', featureType: 'EDI' },
      { label: '受注一覧', to: '/orders' },
      { label: '受注明細一覧', to: '/orders/items' },
      { label: '出荷予定', to: '/orders/shipment-schedule' },
      { label: '受注集計', to: '/orders/items-stats' },
      { label: '受注カレンダー', to: '/orders/calendar' },
      { label: '売上一覧', to: '/orders/sales' },
      { label: '売上明細一覧', to: '/orders/sales/items' },
      { label: '売上集計', to: '/orders/sales/items-stats' },
      { label: '締め', to: '/orders/closings' },
      { label: '請求書', to: '/orders/invoices' },
      { label: '売掛一覧', to: '/orders/accounts-receivable' },
      { label: '入金', to: '/orders/incoming-payments' },
    ],
  },
  {
    label: '発注',
    menuType: 'PURCHASE_ORDERS',
    pages: [
      { label: '発注一覧', to: '/purchase-orders' },
      { label: '発注明細一覧', to: '/purchase-orders/items' },
      { label: '入荷予定一覧', to: '/purchase-orders/arrivals' },
      { label: '仕入一覧', to: '/purchase-orders/purchases' },
      { label: '仕入明細一覧', to: '/purchase-orders/purchases/items' },
      { label: '締め', to: '/purchase-orders/closings' },
      { label: '支払請求', to: '/purchase-orders/purchase-invoices' },
      { label: '買掛一覧', to: '/purchase-orders/accounts-payable' },
      { label: '出金', to: '/purchase-orders/outgoing-payments' },
    ],
  },
  {
    label: '在庫',
    menuType: 'INVENTORY',
    pages: [
      { label: '在庫一覧', to: '/inventory' },
      { label: 'ロット', to: '/inventory/lots', uiFeatureFlag: FeatureFlagType.InventoryLot },
      { label: '在庫調整', to: '/inventory/stock-adjustment' },
      { label: '棚卸', to: '/inventory/stocktaking' },
    ],
  },
  {
    label: '生産',
    menuType: 'MANUFACTURING',
    pages: [{ label: '生産発注', to: '/manufacturing-orders' }],
  },
  {
    label: '商品',
    menuType: 'PRODUCTS',
    pages: [
      { label: '商品一覧', to: '/products' },
      { label: '得意先別単価', to: '/products/customer-prices' },
      { label: '数量別単価', to: '/products/quantity-prices' },
      { label: '商品カタログ', to: '/products/catalogs' },
      { label: '価格改定', to: '/products/price-revisions' },
      { label: '商品プリセット', to: '/products/presets' },
    ],
  },
  {
    label: '取引先',
    menuType: 'CUSTOMERS',
    pages: [
      { label: '取引先', to: '/customers' },
      { label: '行動履歴', to: '/customers/activities' },
      { label: '担当者', to: '/customers/contact-persons' },
    ],
  },
  {
    label: '探索',
    menuType: 'EXPLORE',
    pages: [
      { label: '売上分析', to: '/explore' },
      { label: '顧客売上分析', to: '/explore/customers' },
      { label: '商品売上分析', to: '/explore/products' },
      { label: 'ダッシュボード', to: '/explore/dashboard', demoOnly: true },
    ],
  },
  { label: 'インサイト', demoOnly: true, pages: [{ label: 'インサイト', to: '/insights' }] },
];

const normalize = (value: string) => value.trim().toLowerCase();

/** pathname に最も長く前方一致するメニューのパスを返す。 */
export const findActiveMenuPath = (pathname: string, paths: string[]): string | undefined =>
  paths
    .filter((path) => pathname === path || pathname.startsWith(`${path}/`))
    .reduce<string | undefined>(
      (longest, path) => (!longest || path.length > longest.length ? path : longest),
      undefined,
    );

/** ページ名・セクション名・設定のタブ名でメニューを検索する。 */
export const searchMobileMenu = (
  sections: MobileMenuSection[],
  settingsItems: SettingsMenuItem[],
  keyword: string,
): MobileMenuSearchResult[] => {
  const normalizedKeyword = normalize(keyword);
  if (!normalizedKeyword) return [];

  const pageResults = sections.flatMap((section) =>
    section.pages
      .filter((page) =>
        [page.label, section.label].some((target) => normalize(target).includes(normalizedKeyword)),
      )
      .map((page) => ({ label: page.label, to: page.to, context: section.label, external: page.external })),
  );
  const settingsResults = settingsItems
    .filter((item) =>
      [item.label, ...(item.tabs ?? []).flatMap((tab) => [tab.label, ...(tab.subTabs ?? [])])].some(
        (target) => normalize(target).includes(normalizedKeyword),
      ),
    )
    .map((item) => ({ label: item.label, to: item.to, context: '設定' }));

  return [...pageResults, ...settingsResults];
};

/** 最近使ったページの先頭に path を追加し、重複を除いて max 件に切り詰める。 */
export const pushRecentMenuPath = (
  recentPaths: string[],
  path: string,
  max = RECENT_MENU_PAGES_MAX,
): string[] => [path, ...recentPaths.filter((recentPath) => recentPath !== path)].slice(0, max);
